import { getUserDisplayName } from '@utils/user-utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChannelLike = any;

/** Fallback label for an agent participant when no translation is passed. */
export const DEFAULT_AGENT_LABEL = 'Agent';

/**
 * Agent identities carry only `full_name`, which Zero doesn't sync, so their
 * user row reads as nameless ("Unknown"). The membership row's
 * `agentEnabled` flag is the reliable signal: removing an agent deletes the
 * row rather than clearing the flag.
 */
export function isAgentMember(member: ChannelLike): boolean {
  return Boolean(member?.agentEnabled);
}

/** Display name for one conversation member, naming agents `agentLabel`. */
export function memberDisplayName(
  member: ChannelLike,
  agentLabel: string = DEFAULT_AGENT_LABEL
): string {
  if (isAgentMember(member)) return agentLabel;
  return getUserDisplayName(member?.user ?? {});
}

/** Direct messages are labelled by the other participants, never by the
 * stored name snapshot (which was taken from the creator's point of view).
 * Agent participants are named `agentLabel` (once, however many). */
export function channelLabel(
  channel: ChannelLike,
  viewerUserId: string | undefined,
  agentLabel: string = DEFAULT_AGENT_LABEL
): string {
  if (!channel) return '';
  if (channel.dmChannel) {
    const others = (channel.members ?? []).filter(
      (member: ChannelLike) => member.userId !== viewerUserId
    );
    const humans = others
      .filter((member: ChannelLike) => !isAgentMember(member))
      .map((member: ChannelLike) => getUserDisplayName(member.user ?? {}))
      .sort((a: string, b: string) => a.localeCompare(b));
    const hasAgent = others.some(isAgentMember);
    const labels = hasAgent ? [...humans, agentLabel] : humans;
    if (labels.length) return labels.join(', ');
  }
  return channel.name ?? '';
}

/** A DM whose other participants are all agents (the "Ask an agent" 1:1). */
export function isAgentDirectMessage(
  channel: ChannelLike,
  viewerUserId: string | undefined
): boolean {
  if (!channel?.dmChannel) return false;
  const others = (channel.members ?? []).filter(
    (member: ChannelLike) => member.userId !== viewerUserId
  );
  return others.length > 0 && others.every(isAgentMember);
}

export type ChannelKind = 'public' | 'private' | 'dm' | 'group-dm';

export function channelKind(channel: ChannelLike): ChannelKind {
  if (!channel) return 'public';
  if (channel.dmChannel) {
    return (channel.members?.length ?? 0) > 2 ? 'group-dm' : 'dm';
  }
  return channel.isPrivate ? 'private' : 'public';
}
